import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Users,
  Plus,
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Mail,
  Phone,
  Building2,
  UserCheck,
  UserX,
  RefreshCw,
  Download,
  Edit,
  MoreVertical,
  ArrowUpDown,
  ArrowLeft
} from 'lucide-react';
import api, { getFileUrl } from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import * as XLSX from 'xlsx';
import { sanitizeInput, unescapeHTML, sanitizeCsvFormula } from '../../utils/sanitize';
import useGlobalSearch from '../../hooks/useGlobalSearch';

// Common Components
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import Select from '../../components/Common/Select';
import Badge from '../../components/Common/Badge';

// System-Themed In-App Confirmation Modal Component (Dashboard Blue Theme)
const ConfirmationModal = ({ isOpen, title, message, confirmText = 'OK', cancelText = 'Cancel', onConfirm, onCancel }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-[#002147] border border-gray-200 dark:border-[#1a3884]/40 rounded-2xl p-6 max-w-md w-full shadow-2xl relative animate-in fade-in zoom-in-95 duration-150">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{title}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">{message}</p>
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#00152e] hover:bg-gray-200 dark:hover:bg-[#1a3884]/40 rounded-xl transition-colors"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-semibold text-white bg-[#1a3884] hover:bg-[#00152E] dark:bg-[#1a3884] dark:hover:bg-[#00152E] rounded-xl transition-colors shadow-sm focus:ring-2 focus:ring-[#1a3884]"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const UserManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const { searchQuery: searchTerm, setSearchQuery: setSearchTerm, debouncedSearchQuery: debouncedSearch, searchInputRef } = useGlobalSearch('', 400, () => setCurrentPage(1));
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Default sort order: Newest Joined -> Oldest Joined (createdAt desc)
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Actions Dropdown fixed positioning
  const [openDropdown, setOpenDropdown] = useState(null); // userId
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const [activeUserObj, setActiveUserObj] = useState(null);

  const [colleges, setColleges] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [exporting, setExporting] = useState(false);

  // Custom Confirmation Modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });

  // Stat cards calculation
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    pending: 0,
    suspended: 0,
    byRole: {
      admin: 0,
      office_admin: 0,
      college_admin: 0,
      support: 0,
      recruiter: 0
    }
  });

  const todayDate = new Date().toISOString().split('T')[0];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const roleParam = params.get('role');
    if (roleParam) {
      setSelectedRoles([roleParam]);
    } else {
      setSelectedRoles([]);
    }
  }, [location.search]);

  const fetchColleges = async () => {
    try {
      const { data } = await api.get('/colleges?limit=100');
      if (data.success) {
        setColleges(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch colleges:', error);
    }
  };

  // Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedRoles, statusFilter, selectedCollege, dateRange]);

  useEffect(() => {
    fetchUsers();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, selectedRoles, statusFilter, currentPage, sortBy, sortOrder, selectedCollege, dateRange]);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchColleges();
    }
  }, [user]);

  // Close dropdown when clicking outside or scrolling
  useEffect(() => {
    const handleCloseDropdown = () => setOpenDropdown(null);
    if (openDropdown) {
      document.addEventListener('click', handleCloseDropdown);
      window.addEventListener('scroll', handleCloseDropdown, true);
      return () => {
        document.removeEventListener('click', handleCloseDropdown);
        window.removeEventListener('scroll', handleCloseDropdown, true);
      };
    }
  }, [openDropdown]);

  const fetchStats = async () => {
    try {
      const { data } = await api.get('/users/stats');
      if (data.success) {
        setStats(prev => ({ ...prev, ...data.data }));
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setFetchError(false);

      if (dateRange.start && dateRange.end && dateRange.end < dateRange.start) {
        toast.error('"To" date cannot be earlier than "From" date');
        setLoading(false);
        return;
      }

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(selectedRoles.length > 0 && { role: selectedRoles.join(',') }),
        ...(statusFilter && { status: statusFilter }),
        ...(selectedCollege && { college: selectedCollege }),
        ...(dateRange.start && { startDate: dateRange.start }),
        ...(dateRange.end && { endDate: dateRange.end }),
        sortBy,
        sortOrder
      };

      const { data } = await api.get('/users', { params });

      if (data.success) {
        setUsers(data.data);
        setTotalPages(data.totalPages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setFetchError(true); // BUG-USR-015: Track error state
      if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid search or filter request');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view users');
      } else if (error.response?.status === 429) {
        toast.error('Too many requests. Please wait a moment and try again.');
      } else {
        toast.error('Failed to connect to server. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChangeRequest = (userId, targetStatus) => {
    const statusActionLabel = targetStatus === 'active' ? 'Activate' : 'Deactivate';
    setConfirmModal({
      isOpen: true,
      title: `${statusActionLabel} User`,
      message: `Are you sure you want to mark this user as ${targetStatus === 'active' ? 'Active' : 'Inactive'}?`,
      confirmText: 'OK',
      cancelText: 'Cancel',
      onConfirm: () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        executeStatusChange(userId, targetStatus);
      }
    });
  };

  const executeStatusChange = async (id, newStatus) => {
    try {
      const { data } = await api.put(`/users/${id}/status`, { status: newStatus });
      if (data.success) {
        toast.success(`User status updated to ${newStatus}`);
        fetchUsers();
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(error.response?.data?.message || 'Failed to update user status');
    }
  };

  // BUG-USR-005, BUG-USR-012, BUG-USR-016: Preserved multi-page selection, chunked 500-record batching, Last Login column
  const handleExport = async () => {
    try {
      setExporting(true);
      let usersToExport = [];

      // BUG-USR-005: Preserve multi-page selected users by querying selected IDs if present
      if (selectedUsers.length > 0) {
        const chunkSize = 500;
        for (let i = 0; i < selectedUsers.length; i += chunkSize) {
          const chunkIds = selectedUsers.slice(i, i + chunkSize);
          const { data } = await api.get('/users', { params: { ids: chunkIds.join(','), limit: chunkSize } });
          if (data.success && data.data) {
            usersToExport.push(...data.data);
          }
        }
        if (usersToExport.length === 0) {
          usersToExport = users.filter(u => selectedUsers.includes(u._id));
        }
      } else {
        // BUG-USR-016: Chunked batch retrieval in 500-record batches to avoid thread lock
        let page = 1;
        let totalChunkPages = 1;
        const chunkSize = 500;

        do {
          const params = {
            page,
            limit: chunkSize,
            ...(debouncedSearch && { search: debouncedSearch }),
            ...(selectedRoles.length > 0 && { role: selectedRoles.join(',') }),
            ...(statusFilter && { status: statusFilter }),
            ...(selectedCollege && { college: selectedCollege }),
            ...(dateRange.start && { startDate: dateRange.start }),
            ...(dateRange.end && { endDate: dateRange.end }),
            sortBy,
            sortOrder
          };
          const { data } = await api.get('/users', { params });
          if (data.success && data.data) {
            usersToExport.push(...data.data);
            totalChunkPages = data.totalPages || 1;
          } else {
            break;
          }
          page++;
        } while (page <= totalChunkPages && page <= 20); // Cap at 10,000 max
      }

      if (usersToExport.length === 0) {
        toast.error('No users found to export');
        return;
      }

      // BUG-USR-012: Includes Last Login column in export dataset
      const exportData = usersToExport.map(u => ({
        'User ID': sanitizeCsvFormula(unescapeHTML(u.userId || 'N/A')),
        'Full Name': sanitizeCsvFormula(unescapeHTML(u.fullName || 'N/A')),
        'Email': sanitizeCsvFormula(unescapeHTML(u.email || 'N/A')),
        'Mobile': sanitizeCsvFormula(unescapeHTML(u.mobile || 'N/A')),
        'Role': sanitizeCsvFormula(unescapeHTML(u.role ? u.role.replace('_', ' ').toUpperCase() : 'N/A')),
        'Institute': sanitizeCsvFormula(unescapeHTML(
          u.college?.collegeName || (['admin', 'office_admin'].includes(u.role) ? 'All Institutes' : 'Not Assigned')
        )),
        'Status': sanitizeCsvFormula(unescapeHTML(u.status || 'N/A')),
        'Qualification': sanitizeCsvFormula(unescapeHTML(u.qualification || 'N/A')),
        'Specialization': sanitizeCsvFormula(unescapeHTML(u.specialization || 'N/A')),
        'Created Date': u.createdAt ? new Date(u.createdAt).toLocaleDateString() : 'N/A',
        'Last Login': u.lastLogin ? new Date(u.lastLogin).toLocaleString() : 'Never'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Users');
      XLSX.writeFile(wb, `users_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(`Exported ${exportData.length} user(s)`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export users');
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handleRoleFilterClick = (roleValue) => {
    if (selectedRoles.includes(roleValue)) {
      setSelectedRoles([]);
    } else {
      setSelectedRoles([roleValue]);
    }
  };

  const handleDateChange = (type, val) => {
    if (val > todayDate) {
      toast.error('Future dates are not allowed');
      return;
    }
    if (type === 'start') {
      if (dateRange.end && val > dateRange.end) {
        toast.error('"From" date cannot be after "To" date');
        return;
      }
      setDateRange(prev => ({ ...prev, start: val }));
    } else {
      if (dateRange.start && val < dateRange.start) {
        toast.error('"To" date cannot be before "From" date');
        return;
      }
      setDateRange(prev => ({ ...prev, end: val }));
    }
  };

  // Actions Dropdown positioning right beside clicked row
  const handleToggleDropdown = (e, u) => {
    e.stopPropagation();
    if (openDropdown === u._id) {
      setOpenDropdown(null);
      setActiveUserObj(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const topPos = rect.bottom + 90 > window.innerHeight ? rect.top - 85 : rect.bottom + 4;
      setDropdownPos({
        top: topPos,
        right: window.innerWidth - rect.right
      });
      setOpenDropdown(u._id);
      setActiveUserObj(u);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { variant: 'success', icon: CheckCircle },
      inactive: { variant: 'default', icon: XCircle },
      pending: { variant: 'warning', icon: Clock },
      suspended: { variant: 'danger', icon: AlertCircle }
    };
    const { variant, icon: Icon } = config[status] || config.inactive;

    return (
      <Badge variant={variant}>
        <Icon className="w-3 h-3 mr-1" />
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'N/A'}
      </Badge>
    );
  };

  const getRoleBadge = (role) => {
    const labelMap = {
      admin: 'SMAART ADMIN',
      office_admin: 'OFFICE ADMIN',
      college_admin: 'INSTITUTE ADMIN',
      support: 'IT SUPPORT',
      recruiter: 'RECRUITER'
    };
    return <Badge variant="indigo">{labelMap[role] || (role ? role.replace('_', ' ').toUpperCase() : 'USER')}</Badge>;
  };

  // Stat Card Component - Aligned with Dashboard / SummaryCard styling
  const StatCard = ({ icon: Icon, label, value }) => (
    <div className="group relative bg-white dark:bg-[#002147] rounded-xl p-4 border border-gray-100 dark:border-[#1a3884]/35 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.01] overflow-hidden cursor-pointer">
      <div className="absolute inset-0 bg-gradient-to-br from-[#00152E] to-[#1a3884] opacity-0 group-hover:opacity-[0.03] transition-opacity duration-300"></div>
      <div className="relative flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide truncate mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value || 0}</p>
        </div>
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] flex items-center justify-center shadow-md shadow-[#1a3884]/20 group-hover:scale-110 transition-transform duration-300 shrink-0 text-white">
          <Icon size={18} />
        </div>
      </div>
    </div>
  );

  const renderPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);
    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all ${
            currentPage === i
              ? 'bg-[#1a3884] text-white shadow-sm'
              : 'bg-white dark:bg-[#00152e] text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-[#1a3884]/40 hover:bg-[#1a3884]/10 hover:text-[#1a3884] dark:hover:text-blue-300'
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div className="space-y-6 pb-24 relative animate-fade-in">
      {/* Header */}
      <div className="glass dark:bg-[#002147]/80 rounded-xl shadow-md border border-white/30 dark:border-[#1a3884]/30 p-4 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            {/* BUG-USR-011: Use browser history navigate(-1) */}
            <button
              onClick={() => navigate(-1)}
              className="p-2.5 bg-white dark:bg-[#002147] text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-blue-400 hover:bg-[#00152E]/5 dark:hover:bg-[#1a3884]/30 rounded-xl transition-all border border-gray-200 dark:border-[#1a3884]/45 group shadow-sm flex items-center justify-center shrink-0"
              title="Back"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#00152E] dark:text-white mb-1 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#1a3884] dark:text-blue-400" />
                User Management
              </h1>
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <span className="font-medium">Manage all users and their access</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* BUG-USR-009: Refresh re-fetches both users table and stat cards */}
            <Button
              variant="outline"
              icon={RefreshCw}
              onClick={() => { fetchUsers(); fetchStats(); }}
              size="sm"
              className="px-3 py-1.5 text-[11px] h-auto border-gray-200/50 dark:border-[#1a3884]/50 hover:border-[#1a3884] dark:hover:border-blue-400 text-[#1a3884] dark:text-blue-300"
            >
              <span className='hidden sm:inline'>Refresh</span>
            </Button>
            <Button
              variant="outline"
              icon={Download}
              onClick={handleExport}
              size="sm"
              disabled={exporting}
              className="px-3 py-1.5 text-[11px] h-auto border-gray-200/50 dark:border-[#1a3884]/50 hover:border-[#1a3884] dark:hover:border-blue-400 text-[#1a3884] dark:text-blue-300 disabled:opacity-50"
            >
              <span className='hidden sm:inline'>{exporting ? 'Exporting...' : 'Export'}</span>
            </Button>

            <button
              onClick={() => navigate('/users/create')}
              className="flex items-center gap-2 text-sm px-4 py-2 bg-[#1a3884] hover:bg-[#00152E] dark:bg-[#1a3884] dark:hover:bg-[#00152E] text-white font-semibold rounded-xl transition-colors shadow-sm border border-[#00152E]/20"
            >
              <Plus size={14} />
              <span className='hidden sm:inline'>Add User</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stat Cards - Dashboard Theme Aligned */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Users" value={stats.total} />
        <StatCard icon={CheckCircle} label="Active" value={stats.active} />
        <StatCard icon={XCircle} label="Inactive" value={stats.inactive} />
        <StatCard icon={Clock} label="Pending Approval" value={stats.pending} />
      </div>

      {/* Filter Section - Dashboard Aligned */}
      <div className="bg-white dark:bg-[#002147] rounded-xl p-4 shadow-sm border border-gray-100 dark:border-[#1a3884]/35">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left 50% Column */}
            <div className="space-y-3">
              {/* Row 1, Left: Search Box */}
              <div className="relative w-full">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search by name, email, phone, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full h-[42px] pl-10 pr-10 border border-gray-200 dark:border-[#1a3884]/50 rounded-lg text-sm bg-white dark:bg-[#00152e] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-blue-400 focus:border-[#1a3884] transition-all duration-200"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#1a3884] dark:hover:text-blue-400 p-0.5 rounded-full hover:bg-gray-100 dark:hover:bg-[#1a3884]/40 transition-colors"
                    title="Clear search"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Row 2, Left: All Status Dropdown */}
              <div className="relative w-full">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full h-[42px] px-3.5 border border-gray-200 dark:border-[#1a3884]/50 rounded-lg text-sm bg-white dark:bg-[#00152e] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-blue-400 focus:border-[#1a3884] transition-all duration-200 cursor-pointer"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending Approval</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>

            {/* Right 50% Column */}
            <div className="space-y-3">
              {/* Row 1, Right: From Date Picker with Inline Label */}
              <div className="relative flex items-center w-full h-[42px] border border-gray-200 dark:border-[#1a3884]/50 rounded-lg bg-white dark:bg-[#00152e] focus-within:ring-2 focus-within:ring-[#1a3884] dark:focus-within:ring-blue-400 focus-within:border-[#1a3884] transition-all duration-200">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 pl-3.5 pr-1 select-none shrink-0">From</span>
                <input
                  type="date"
                  value={dateRange.start}
                  max={todayDate}
                  onChange={(e) => handleDateChange('start', e.target.value)}
                  className="w-full h-full pr-3 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none cursor-pointer border-none"
                  placeholder="From Date"
                />
              </div>

              {/* Row 2, Right: To Date Picker with Inline Label */}
              <div className="relative flex items-center w-full h-[42px] border border-gray-200 dark:border-[#1a3884]/50 rounded-lg bg-white dark:bg-[#00152e] focus-within:ring-2 focus-within:ring-[#1a3884] dark:focus-within:ring-blue-400 focus-within:border-[#1a3884] transition-all duration-200">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 pl-3.5 pr-1 select-none shrink-0">To</span>
                <input
                  type="date"
                  value={dateRange.end}
                  min={dateRange.start || undefined}
                  max={todayDate}
                  onChange={(e) => handleDateChange('end', e.target.value)}
                  className="w-full h-full pr-3 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none cursor-pointer border-none"
                  placeholder="To Date"
                />
              </div>
            </div>
          </div>

          {/* Role Filter Buttons */}
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100 dark:border-[#1a3884]/20">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mr-1">Role Filter:</span>
            <button
              onClick={() => setSelectedRoles([])}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                selectedRoles.length === 0
                  ? 'bg-[#1a3884] text-white border border-[#1a3884] shadow-sm'
                  : 'bg-gray-50 dark:bg-[#00152e] text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-[#1a3884]/40 hover:border-[#1a3884]/60 hover:text-[#1a3884] dark:hover:text-blue-300'
              }`}
            >
              All Users ({stats.total})
              {selectedRoles.length === 0 && <span className="ml-1">✓</span>}
            </button>
            {[
              { value: 'admin', label: 'SMAART Admin', count: stats.byRole.admin },
              { value: 'office_admin', label: 'Office Admin', count: stats.byRole.office_admin || 0 },
              { value: 'college_admin', label: 'Institute Admin', count: stats.byRole.college_admin },
              { value: 'recruiter', label: 'Recruiters', count: stats.byRole.recruiter },
              { value: 'support', label: 'IT Support', count: stats.byRole.support }
            ].map((role) => {
              const isSelected = selectedRoles.includes(role.value);
              return (
                <button
                  key={role.value}
                  onClick={() => handleRoleFilterClick(role.value)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                    isSelected
                      ? 'bg-[#1a3884] text-white border border-[#1a3884] shadow-sm'
                      : 'bg-gray-50 dark:bg-[#00152e] text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-[#1a3884]/40 hover:border-[#1a3884]/60 hover:text-[#1a3884] dark:hover:text-blue-300'
                  }`}
                >
                  {role.label} ({role.count})
                  {isSelected && <span className="ml-1">✓</span>}
                </button>
              );
            })}
            <div className="ml-auto">
              <Button
                variant="outline"
                size="sm"
                className="text-xs py-1 h-auto border-gray-200 dark:border-[#1a3884]/40 text-[#1a3884] dark:text-blue-300 hover:border-[#1a3884]"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedRoles([]);
                  setStatusFilter('');
                  setSelectedCollege('');
                  setDateRange({ start: '', end: '' });
                }}
              >
                Reset Filters
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* BUG-USR-015: Dedicated Connection Error Card with Retry Button */}
      {fetchError ? (
        <div className="p-8 text-center bg-white dark:bg-[#002147] rounded-xl border border-red-200 dark:border-red-900/40 shadow-sm my-4 animate-fade-in">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3 animate-bounce" />
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Failed to load users</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">There was a server or connection error while retrieving the user list.</p>
          <Button variant="outline" onClick={() => { setFetchError(false); fetchUsers(); fetchStats(); }} icon={RefreshCw}>
            Retry Connection
          </Button>
        </div>
      ) : (
        /* Main User Table */
        <div className="bg-white dark:bg-[#002147] rounded-xl shadow-sm border border-gray-100 dark:border-[#1a3884]/30 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1180px]">
              <thead className="bg-gray-50 dark:bg-[#001f40]/80 border-b border-gray-200 dark:border-[#1a3884]/20">
                <tr>
                  {['fullName', 'Contact', 'role', 'Institute', 'status', 'createdAt', 'lastLogin'].map(col => {
                    const isSortable = col !== 'Contact' && col !== 'Institute';
                    const isCurrentSort = sortBy === col;
                    return (
                      <th
                        key={col}
                        aria-sort={isSortable ? (isCurrentSort ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
                        className={`px-4 py-3 text-left text-[11px] font-semibold text-gray-700 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap ${isSortable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-[#00152e]' : ''}`}
                        onClick={() => isSortable && handleSort(col)}
                      >
                        <div className="flex items-center gap-1">
                          {col === 'createdAt' ? 'Joined' : col === 'fullName' ? 'User Details' : col.charAt(0).toUpperCase() + col.slice(1)}
                          {isCurrentSort && (
                            <ArrowUpDown className={`w-3 h-3 text-[#1a3884] dark:text-blue-400 ${sortOrder === 'asc' ? 'rotate-180 transition-transform' : ''}`} />
                          )}
                        </div>
                      </th>
                    );
                  })}
                  <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-700 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-[#1a3884]/20">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a3884]"></div>
                      </div>
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <Users className="w-12 h-12 text-gray-400 mb-3" />
                        <p className="text-gray-900 dark:text-white font-medium mb-1">No Users Found</p>
                        <p className="text-xs text-gray-500 mb-3">Try clearing search or filters to see more results.</p>
                        <Button variant="outline" size="sm" onClick={() => { setSearchTerm(''); setSelectedRoles([]); setStatusFilter(''); }}>Clear Filters</Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr
                      key={u._id}
                      className="hover:bg-blue-50/50 dark:hover:bg-[#001f40]/80 transition-colors"
                    >
                      <td className="px-4 py-3 min-w-[220px]">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-9 w-9 bg-gradient-to-br from-[#00152E] to-[#1a3884] rounded-full flex items-center justify-center shadow-sm overflow-hidden">
                            {u.profileImage ? (
                              <img src={getFileUrl(u.profileImage)} className="w-full h-full object-cover" alt="Profile" />
                            ) : (
                              <span className="text-white font-semibold text-xs">
                                {sanitizeInput(u.fullName?.charAt(0)?.toUpperCase() || 'U')}
                              </span>
                            )}
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight break-words">{sanitizeInput(u.fullName || 'N/A')}</div>
                            {u.role === 'recruiter' && (u.companyId || u.recruiterId) ? (
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {u.companyId && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#00152E] text-white border border-[#00152E] tracking-wide">
                                    {u.companyId}
                                  </span>
                                )}
                                {u.recruiterId && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#1a3884] text-white border border-[#1a3884] tracking-wide">
                                    {u.recruiterId}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{sanitizeInput(u.userId || u.studentId || u.recruiterId || 'N/A')}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 min-w-[240px]">
                        <div className="flex flex-col space-y-1">
                          <div className="flex items-center text-xs text-gray-700 dark:text-slate-300 break-all">
                            <Mail className="w-3.5 h-3.5 mr-1.5 flex-shrink-0 text-[#1a3884] dark:text-blue-400" />
                            {sanitizeInput(u.email)}
                          </div>
                          <div className="flex items-center text-xs text-gray-700 dark:text-slate-300">
                            <Phone className="w-3.5 h-3.5 mr-1.5 flex-shrink-0 text-gray-500 dark:text-slate-400" />
                            {sanitizeInput(u.mobile)}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 min-w-[130px]">
                        {getRoleBadge(u.role)}
                      </td>
                      <td className="px-4 py-3 min-w-[150px]">
                        {['admin', 'office_admin'].includes(u.role) ? (
                          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">All Institutes</span>
                        ) : u.college ? (
                          <div className="flex items-center text-xs text-gray-700 dark:text-slate-300">
                            <Building2 className="w-3.5 h-3.5 mr-1.5 flex-shrink-0 text-[#1a3884] dark:text-blue-400" />
                            {sanitizeInput(u.college.collegeName)}
                          </div>
                        ) : (
                          <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">Not Assigned</span>
                        )}
                      </td>
                      <td className="px-4 py-3 min-w-[120px]">
                        <div className="flex flex-col space-y-1">
                          {getStatusBadge(u.status)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 dark:text-slate-400 whitespace-nowrap">
                        {u.createdAt ? (() => {
                          const date = new Date(u.createdAt);
                          return !isNaN(date) ? date.toLocaleDateString() : 'N/A';
                        })() : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 dark:text-slate-400 min-w-[160px]">
                        {u.lastLogin ? (() => {
                          const date = new Date(u.lastLogin);
                          return !isNaN(date) ? date.toLocaleString() : 'Never';
                        })() : 'Never'}
                      </td>
                      <td className="px-4 py-3 text-right relative min-w-[130px]">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => navigate(`/users/${u._id}`)}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-[#1a3884] dark:text-blue-300 bg-[#1a3884]/10 hover:bg-[#1a3884]/20 rounded-lg transition-colors"
                            title="View User"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span>View</span>
                          </button>
                          
                          {/* BUG-USR-017: Accessibility attributes on 3-dot dropdown button */}
                          <button
                            aria-haspopup="true"
                            aria-expanded={openDropdown === u._id}
                            onClick={(e) => handleToggleDropdown(e, u)}
                            className="p-1.5 text-slate-400 dark:text-slate-300 hover:text-[#1a3884] dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                            title="Actions"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* BUG-USR-013: Numbered Pagination Controls displayed for all results (totalPages >= 1) */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-[#1a3884]/20 flex items-center justify-between flex-wrap gap-3">
            <div className="text-xs font-medium text-gray-500 dark:text-slate-400">
              Showing page {currentPage} of {totalPages} ({stats.total} total users)
            </div>
            <div className="flex items-center space-x-1">
              <Button
                onClick={() => setCurrentPage(c => Math.max(1, c - 1))}
                disabled={currentPage === 1}
                variant="outline"
                size="sm"
                className="text-xs px-2.5 py-1 text-[#1a3884] dark:text-blue-300 border-gray-200 dark:border-[#1a3884]/40"
              >
                Prev
              </Button>
              {renderPageNumbers()}
              <Button
                onClick={() => setCurrentPage(c => Math.min(totalPages, c + 1))}
                disabled={currentPage === totalPages}
                variant="outline"
                size="sm"
                className="text-xs px-2.5 py-1 text-[#1a3884] dark:text-blue-300 border-gray-200 dark:border-[#1a3884]/40"
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Actions Dropdown */}
      {openDropdown && activeUserObj && (
        <div
          style={{ position: 'fixed', top: dropdownPos.top - window.scrollY, right: dropdownPos.right }}
          className="w-44 bg-white dark:bg-[#002147] border border-gray-200 dark:border-[#1a3884]/40 rounded-xl shadow-2xl z-[9999] py-1 animate-in fade-in zoom-in-95 duration-100"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => {
              navigate(`/users/edit/${activeUserObj._id}`);
              setOpenDropdown(null);
            }}
            className="w-full px-4 py-2.5 text-left text-xs text-gray-700 dark:text-slate-200 hover:bg-[#1a3884]/10 hover:text-[#1a3884] dark:hover:bg-[#1a3884]/35 dark:hover:text-blue-300 flex items-center transition-colors font-medium"
          >
            <Edit className="w-3.5 h-3.5 mr-2.5 text-[#1a3884] dark:text-blue-400" />
            Edit User
          </button>
          
          <button
            onClick={() => {
              const targetStatus = activeUserObj.status === 'active' ? 'inactive' : 'active';
              handleStatusChangeRequest(activeUserObj._id, targetStatus);
              setOpenDropdown(null);
            }}
            className="w-full px-4 py-2.5 text-left text-xs text-gray-700 dark:text-slate-200 hover:bg-[#1a3884]/10 hover:text-[#1a3884] dark:hover:bg-[#1a3884]/35 dark:hover:text-blue-300 flex items-center transition-colors font-medium"
          >
            {activeUserObj.status === 'active' ? (
              <>
                <UserX className="w-3.5 h-3.5 mr-2.5 text-[#1a3884] dark:text-blue-400" />
                Deactivate
              </>
            ) : (
              <>
                <UserCheck className="w-3.5 h-3.5 mr-2.5 text-[#1a3884] dark:text-blue-400" />
                Activate
              </>
            )}
          </button>
        </div>
      )}

      {/* Confirmation Modal Component */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default UserManagement;
